import {
  MousePointerClick, Contact, Send, KanbanSquare, Fingerprint, BarChart3, Globe, Lightbulb, Activity, PlugZap,
} from "lucide-react";

const ACCENT = "#BEF264";

const FEATURES = [
  { Icon: MousePointerClick, title: "Website & Lead Capture", copy: "Business websites, landing pages, quotation forms and customer enquiry journeys.", tier: "core" },
  { Icon: Contact, title: "Customer Relationship Management", copy: "Contact records, customer information, interaction history and lead organisation.", tier: "core" },
  { Icon: Send, title: "Automated Follow-Up", copy: "Automated acknowledgement emails, nurture journeys, reminders and configured follow-up workflows.", tier: "core" },
  { Icon: KanbanSquare, title: "Sales Pipeline", copy: "Opportunity stages, sales ownership, next actions and deal tracking.", tier: "core" },
  { Icon: Fingerprint, title: "Marketing Attribution", copy: "Tracking available traffic sources, campaign information and customer actions.", tier: "core" },
  { Icon: BarChart3, title: "Sales Performance Visibility", copy: "Dashboards and reporting that help businesses monitor enquiries and opportunities.", tier: "core" },
];

const ADVANCED = [
  { Icon: PlugZap, title: "Optional integrations", copy: "Connections to existing CRMs, booking tools or other platforms — scoped per project." },
  { Icon: Lightbulb, title: "AI improvement advisor", copy: "Plain-language recommendations where sufficient data is available. Advanced, scoped separately." },
  { Icon: Activity, title: "Behavioural analytics", copy: "Heatmaps and session recordings where configured and legally permitted. May require third-party software." },
];

function SystemVisual() {
  return (
    <div className="relative rounded-3xl border border-white/10 bg-white/[0.04] p-5 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.8)]">
      <div className="rounded-xl border border-white/10 bg-white/[0.05] p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="h-2 w-2 rounded-full bg-white/20" /><span className="h-2 w-2 rounded-full bg-white/20" /><span className="h-2 w-2 rounded-full bg-white/20" />
          <div className="ml-2 flex items-center gap-1.5 rounded bg-white/[0.06] px-2 py-0.5 text-[9px] font-mono text-white/50">
            <Globe className="h-2.5 w-2.5" /> yourbusiness.co.za
          </div>
        </div>
        <div className="rounded-lg bg-white p-3">
          <div className="h-2 w-24 rounded bg-zinc-200" />
          <div className="mt-2 h-1.5 w-40 rounded bg-zinc-100" />
          <div className="mt-3 inline-block rounded-md px-3 py-1 text-[9px] font-bold text-zinc-900" style={{ background: ACCENT }}>Get a quote</div>
        </div>
      </div>
      <div className="flex justify-center py-2"><div className="h-6 w-px" style={{ background: `linear-gradient(${ACCENT}, transparent)` }} /></div>
      <div className="rounded-xl border border-white/10 bg-white/[0.05] p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/50 mb-2">CRM · New enquiries</div>
        {[
          { i: "NK", n: "Naledi K.", s: "Google Ads", st: "New" },
          { i: "TM", n: "Thabo M.", s: "Meta", st: "Contacted" },
          { i: "LP", n: "Lerato P.", s: "Referral", st: "Qualified" },
        ].map((r) => (
          <div key={r.i} className="flex items-center justify-between py-1.5 border-t border-white/5 first:border-0">
            <div className="flex items-center gap-2">
              <span className="h-6 w-6 rounded-full flex items-center justify-center text-[8px] font-black text-zinc-900" style={{ background: ACCENT }}>{r.i}</span>
              <div>
                <div className="text-[11px] font-bold text-white leading-none">{r.n}</div>
                <div className="text-[8px] text-white/40 mt-0.5">{r.s}</div>
              </div>
            </div>
            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full border border-white/15 text-white/60">{r.st}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-center text-[10px] uppercase tracking-[0.16em] text-white/35">Illustrative example · fictional data</p>
    </div>
  );
}

export default function SalesCapabilities() {
  return (
    <section id="features" aria-labelledby="features-heading" data-testid="web-benefits-section" className="relative py-20 sm:py-28 bg-sales-navy text-white overflow-hidden">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-white/70 font-semibold mb-6">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: ACCENT }} /> Website with CRM
          </div>
          <h2 id="features-heading" className="font-display text-4xl sm:text-5xl lg:text-[52px] font-black tracking-[-0.03em] leading-[1.03]">
            Everything Your Business Needs to Capture and Manage Sales Enquiries
          </h2>
        </div>

        <div className="mt-14 grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-28"><SystemVisual /></div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: ACCENT }}>Core features · included in your implementation</div>
            <div className="grid sm:grid-cols-2 gap-3.5">
              {FEATURES.map(({ Icon, title, copy }, i) => (
                <div key={title} data-testid={`sales-feature-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:bg-white/[0.07]">
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-white/[0.06]"><Icon className="h-4 w-4" style={{ color: ACCENT }} /></div>
                    <h3 className="font-display text-[15px] font-black text-white leading-tight">{title}</h3>
                  </div>
                  <p className="mt-2.5 text-[13px] leading-snug text-white/60">{copy}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-[11px] font-bold uppercase tracking-[0.18em] text-white/50 mb-3">Optional &amp; advanced · scoped per project</div>
            <div className="grid sm:grid-cols-3 gap-3">
              {ADVANCED.map(({ Icon, title, copy }, i) => (
                <div key={title} data-testid={`sales-feature-advanced-${i}`} className="rounded-2xl border border-dashed border-white/15 p-4">
                  <div className="flex items-center gap-2 text-[13px] font-bold text-white/85"><Icon className="h-4 w-4 text-white/50" /> {title}</div>
                  <p className="mt-2 text-[12px] leading-snug text-white/50">{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p data-testid="sales-features-note" className="mt-10 text-xs text-white/45 max-w-3xl leading-relaxed">
          Core features are configured to the scope agreed in your proposal. Optional integrations and advanced features are
          quoted separately, and some depend on third-party software subscriptions, platform compatibility and consent requirements.
        </p>
      </div>
    </section>
  );
}
